import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  User, Star, Video, Camera, Calendar, LogOut, Edit, Eye, 
  Plus, Menu, X, Home, Users, Mic, Film, MessageSquare,
  GraduationCap, Crown, ChevronRight, Bell, Search, Sparkles,
  Handshake, CheckCircle, XCircle, Share2, Copy
} from "lucide-react";
import { handleShare as safeHandleShare, getProfileShareUrlAsync, safeCopyToClipboard } from "@/utils/shareHelper";
import AdvertisementBanner from "@/components/AdvertisementBanner";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import ProfileCompleteness from "@/components/ProfileCompleteness";
import NotificationBell from "@/components/NotificationBell";
import EventsSection from "@/components/EventsSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Services menu items for Talent
const SERVICES = [
  {
    id: "discover",
    title: "Discover Talents",
    description: "Browse talents by category",
    icon: Users,
    color: "bg-gradient-to-r from-violet-500 to-pink-500",
    link: "/browse",
    badge: "Featured"
  },
  {
    id: "services",
    title: "Opportunities",
    description: "Browse all opportunities",
    icon: Search,
    color: "bg-indigo-500",
    link: "/services",
    badge: "New"
  },
  {
    id: "meetings",
    title: "Meetings",
    description: "Schedule meetings with agents",
    icon: Video,
    color: "bg-blue-500",
    link: "/meeting-room",
    badge: null
  },
  {
    id: "auditions",
    title: "Auditions",
    description: "Virtual audition rooms",
    icon: Film,
    color: "bg-violet-500",
    link: "/audition-room",
    badge: "2 Upcoming"
  },
  {
    id: "live-school",
    title: "Live School",
    description: "Learn from experts",
    icon: GraduationCap,
    color: "bg-pink-500",
    link: "/live-school",
    badge: "New Classes"
  },
  {
    id: "membership",
    title: "Membership",
    description: "Upgrade your plan",
    icon: Crown,
    color: "bg-amber-500",
    link: "/membership",
    badge: null
  },
  {
    id: "chat",
    title: "Messages",
    description: "Chat with connections",
    icon: MessageSquare,
    color: "bg-green-500",
    link: "/chat",
    badge: "3 New"
  },
  {
    id: "casting",
    title: "Casting Calls",
    description: "Browse opportunities",
    icon: Mic,
    color: "bg-red-500",
    link: "/casting-call",
    badge: "5 Active"
  }
];

export default function TalentDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [linkRequests, setLinkRequests] = useState([]);
  const [myLinks, setMyLinks] = useState([]);
  const [shareModal, setShareModal] = useState({ open: false, url: "" });

  /**
   * Share handler - MUST be called directly in onClick (user gesture)
   * Opens native share sheet on mobile (WhatsApp, etc.)
   * Falls back to copy link if share unavailable
   */
  const handleShare = async () => {
    const shareUrl = await getProfileShareUrlAsync(profile, "talent");
    await safeHandleShare({
      url: shareUrl,
      title: `${profile?.personal_info?.full_name || "Talent"} on TalentMeet`,
      text: `Check out ${profile?.personal_info?.full_name || "this talent"}'s profile on TalentMeet!`,
      onShowModal: (url) => setShareModal({ open: true, url })
    });
  };

  // Copy from modal fallback
  const copyShareUrl = async () => {
    const success = await safeCopyToClipboard(shareModal.url);
    if (success) {
      toast.success("Link copied!");
      setShareModal({ open: false, url: "" });
    } else {
      toast.info("Long press the link to copy");
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // Verify role is talent - redirect if not
      if (parsed.role !== "talent") {
        toast.error("Access denied. Redirecting to your dashboard.");
        window.location.href = `/dashboard/${parsed.role}`;
        return;
      }
      setUser(parsed);
    }
    fetchDashboardData();
  }, []); // Remove navigate from dependencies

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      // Use the role-guarded talent dashboard endpoint
      const response = await fetch(`${API_URL}/api/dashboard/talent/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        const err = await response.json();
        toast.error(err.detail || "Access denied");
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        window.location.href = `/dashboard/${storedUser.role || "talent"}`;
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        setLinkRequests(data.link_requests || []);
        setMyLinks(data.my_links || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToLink = async (linkId, action, relationshipType = null) => {
    try {
      const token = localStorage.getItem("token");
      const body = { action };
      if (relationshipType) body.relationship_type = relationshipType;
      
      const response = await fetch(`${API_URL}/api/links/${linkId}/respond`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(action === "approve" ? "Collaboration approved!" : "Request declined");
        fetchDashboardData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to respond");
      }
    } catch (error) {
      toast.error("Failed to respond to request");
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
    toast.success("Logged out successfully");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-teal-100 text-teal-800 text-xs">Approved</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Pending</Badge>;
      case "suspended":
        return <Badge className="bg-red-100 text-red-800 text-xs">Suspended</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Announcement Bar */}
      <AdvertisementBanner placement="dashboard" format="top_bar" />
      
      {/* Compact Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
              <span className="font-display text-lg font-bold text-gray-900 hidden sm:block">TalentMeet</span>
              <Badge className="bg-violet-100 text-violet-700 ml-2">Talent</Badge>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/dashboard/talent" className="px-3 py-2 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg">
                Dashboard
              </Link>
              <Link to="/browse" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">
                Browse
              </Link>
              <Link to="/chat" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg relative">
                Messages
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">3</span>
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-gray-500">
                <LogOut className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 p-3">
            <nav className="space-y-1">
              <Link to="/dashboard/talent" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-violet-50 text-violet-600">
                <Home className="w-5 h-5" /> Dashboard
              </Link>
              <Link to="/browse" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <Users className="w-5 h-5" /> Browse Talents
              </Link>
              <Link to="/chat" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <MessageSquare className="w-5 h-5" /> Messages
                <Badge className="ml-auto bg-red-500 text-white text-xs">3</Badge>
              </Link>
              <Link to="/live-school" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <GraduationCap className="w-5 h-5" /> Live School
              </Link>
              <Link to="/membership" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <Crown className="w-5 h-5" /> Membership
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl p-5 mb-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold mb-1">Welcome back, {user?.name?.split(" ")[0]}!</h1>
              <p className="text-white/80 text-sm">
                {profile ? "Manage your profile and explore opportunities" : "Complete your profile to get started"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">Talent Dashboard</span>
            </div>
          </div>
        </div>

        {/* Services Menu - PROMINENT */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Services</h2>
            <Link to="/browse" className="text-sm text-violet-600 hover:underline flex items-center gap-1">
              Browse All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {SERVICES.map(service => (
              <Link
                key={service.id}
                to={service.link}
                className="group bg-white rounded-xl p-4 border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all"
                data-testid={`service-${service.id}`}
              >
                <div className={`w-10 h-10 ${service.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <service.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-medium text-gray-900 text-sm mb-1">{service.title}</h3>
                <p className="text-xs text-gray-500 mb-2">{service.description}</p>
                {service.badge && (
                  <Badge variant="secondary" className="text-xs">{service.badge}</Badge>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* Profile Section */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {!profile ? (
              /* Create Profile CTA */
              <Card className="border-violet-200 bg-gradient-to-r from-violet-50 to-pink-50">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <User className="w-7 h-7 text-violet-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 mb-2">Complete Your Profile</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Create your talent profile to get discovered by casting directors and agencies.
                  </p>
                  <Button 
                    onClick={() => navigate("/talent/profile/create")}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="create-profile-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Create Profile
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Profile Overview */
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Your Profile</CardTitle>
                    <div className="flex items-center gap-2">
                      {profile.is_draft && <Badge variant="outline" className="text-xs">Draft</Badge>}
                      {getStatusBadge(profile.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    {/* Profile Photo */}
                    <div className="relative flex-shrink-0">
                      {profile.portfolio?.profile_photo ? (
                        <div className="talent-thumb-container w-20 h-24 rounded-xl overflow-hidden">
                          <img 
                            src={profile.portfolio.profile_photo} 
                            alt={profile.personal_info?.display_name}
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-24 rounded-xl bg-gradient-to-br from-violet-400 to-pink-400 flex items-center justify-center">
                          <Camera className="w-8 h-8 text-white/80" />
                        </div>
                      )}
                      {profile.verification_status === 'verified' && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Star className="w-3 h-3 text-white fill-white" />
                        </div>
                      )}
                    </div>

                    {/* Profile Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">
                        {profile.personal_info?.display_name || profile.personal_info?.full_name || "Your Name"}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {profile.professional_info?.primary_category || "No category set"}
                      </p>
                      
                      {/* Profile Completion */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">Profile Completion</span>
                          <span className="font-medium text-violet-600">{profile.completion_percentage || 0}%</span>
                        </div>
                        <Progress value={profile.completion_percentage || 0} className="h-1.5" />
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => navigate("/talent/profile/edit")}
                          className="bg-violet-600 hover:bg-violet-700"
                          data-testid="edit-profile-btn"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/profile/${profile.profile_id}`)}
                          data-testid="view-profile-btn"
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={handleShare}
                          data-testid="share-profile-btn"
                          title="Copy shareable profile link"
                        >
                          <Share2 className="w-3 h-3 mr-1" /> Share
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Eye className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Profile viewed</p>
                      <p className="text-xs text-gray-500">Your profile was viewed 12 times this week</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">+5</Badge>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">New messages</p>
                      <p className="text-xs text-gray-500">You have 3 unread messages</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => navigate("/chat")}>
                      View <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Mic className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">New casting calls</p>
                      <p className="text-xs text-gray-500">5 new opportunities match your profile</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => navigate("/casting-call")}>
                      View <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Profile Completeness */}
            <ProfileCompleteness profile={profile} />

            {/* Upcoming Events */}
            <EventsSection userRole="talent" title="Upcoming Events" showCreateButton={false} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Collaboration Requests - Show for Talent/Trainer */}
            {linkRequests.length > 0 && (
              <Card className="border-violet-200 bg-violet-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-violet-600" />
                    Collaboration Requests
                    <Badge className="ml-auto bg-violet-600 text-white">{linkRequests.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {linkRequests.slice(0, 3).map(req => (
                    <div key={req.link_id} className="p-3 bg-white rounded-lg border border-violet-100">
                      <p className="font-medium text-gray-900 text-sm">{req.enterprise_name}</p>
                      {req.message && <p className="text-xs text-gray-500 mt-1">{req.message}</p>}
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          className="flex-1 bg-violet-600 hover:bg-violet-700 h-8 text-xs"
                          onClick={() => handleRespondToLink(req.link_id, "approve", "in_collaboration_with")}
                          data-testid={`approve-link-${req.link_id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => handleRespondToLink(req.link_id, "reject")}
                          data-testid={`reject-link-${req.link_id}`}
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* My Collaborations */}
            {myLinks.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    My Collaborations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {myLinks.slice(0, 5).map(link => (
                    <div key={link.link_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <Handshake className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{link.enterprise_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{link.relationship_type?.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Stats Card */}
            {profile && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-violet-50 rounded-lg">
                      <p className="text-2xl font-bold text-violet-600">{profile.average_rating?.toFixed(1) || "0.0"}</p>
                      <p className="text-xs text-gray-500">Rating</p>
                    </div>
                    <div className="text-center p-3 bg-pink-50 rounded-lg">
                      <p className="text-2xl font-bold text-pink-600">{profile.total_ratings || 0}</p>
                      <p className="text-xs text-gray-500">Reviews</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">12</p>
                      <p className="text-xs text-gray-500">Views</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{myLinks.length}</p>
                      <p className="text-xs text-gray-500">Collaborations</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Upcoming</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Video className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Audition</p>
                      <p className="text-xs text-gray-500">Tomorrow, 4:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Acting Workshop</p>
                      <p className="text-xs text-gray-500">Jan 5, 6:00 PM</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4" size="sm">
                  <Calendar className="w-4 h-4 mr-2" /> View Calendar
                </Button>
              </CardContent>
            </Card>

            {/* Upgrade CTA */}
            <Card className="bg-gradient-to-br from-violet-500 to-pink-500 text-white border-0">
              <CardContent className="p-4">
                <Crown className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">Upgrade to Pro</h3>
                <p className="text-sm text-white/80 mb-3">Get verified badge and priority visibility</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-violet-600 hover:bg-gray-100"
                  onClick={() => navigate("/membership")}
                >
                  View Plans
                </Button>
              </CardContent>
            </Card>
            
            {/* Sidebar Ad */}
            <AdvertisementBanner placement="dashboard" format="sidebar" />
          </div>
        </div>
      </main>

      {/* Share Link Modal - Fallback when native share unavailable */}
      <Dialog open={shareModal.open} onOpenChange={(open) => setShareModal({ ...shareModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-violet-600" />
              Share Your Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copy this link to share on WhatsApp, Instagram, or anywhere:
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={shareModal.url} 
                readOnly 
                className="text-sm font-mono bg-gray-50"
                onClick={(e) => e.target.select()}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copyShareUrl} size="sm" className="shrink-0">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Tap the link to select, then tap Copy button
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* WhatsApp Support */}
      <WhatsAppSupport />
    </div>
  );
}
